import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useState } from "react";
import { Product, useCartStore } from "../../../store/cart-store";
import { usePaymentMethods } from "./use-payment-methods";
import React from "react";

// Definición de tipos para la orden
export interface OrderItem {
  product_id: string;
  quantity: number;
}
export interface IReceiver {
  type: 'self' | 'other';
  name?: string;
  document?: string;
  phone?: string;
}
export interface ShippingInfo {
  name: string;
  phone: string;
  country_id: string;
  province_id: string;
  city_id: string;
  address: string;
  postal_code: string;
  neighborhood?: string;
  /** IdLocalidad de Inter; vacío = la ciudad. */
  neighborhood_code?: string;
  observations?: string;
  receiver: IReceiver;
}



export interface OrderData {
  address_id?: string | null;
  items: OrderItem[];
  shipping: ShippingInfo | null;
  payment_provider?: string;
  payment_method?: string;
  delivery_type?: string;
  currency?: string;
  payment_channel?:string;
  carrier_id?: string;
  shipping_cost?: number;
}

export interface CheckoutData {
  addressId: string | null;
  shippingInfo: ShippingInfo | null;
  paymentMethod: string;
  deliveryType: 'DELIVERY' | 'STORE_PICKUP';
  carrierId?: string;
  shippingCost?: number;
}

export interface PaymentMethod {
  id: string;
  name: string;
  logo: string;
  description: string;
}

/**
 * Hook personalizado para guardar órdenes en el endpoint /order
 * @returns Objeto con la función processOrder y el estado de la mutación
 */
export const useSaveOrder = (onPaymentMethodSelect?: (method: string) => void) => {

  // Definición de los métodos de pago disponibles usando useMemo para evitar recreaciones en cada render
  const availablePaymentMethods = React.useMemo<Record<string, PaymentMethod>>(() => ({
    wompi: {
      id: 'wompi',
      name: 'Wompi',
      logo: '/images/payment/wompi-logo.png',
      description: 'Paga de forma segura con Wompi, acepta múltiples métodos de pago en Colombia.'
    },
    coinpayment: {
      id: 'coinpayment',
      name: 'CoinPayment',
      logo: '/images/payment/coinpayment-logo.svg',
      description: 'Paga con criptomonedas de forma segura con CoinPayment.'
    },
    stripe: {
      id: 'stripe',
      name: 'Stripe',
      logo: '/images/payment/stripe-logo.png',
      description: 'Paga con tarjeta de crédito o débito de forma segura con Stripe.'
    }
  }), []);

  const { postData } = useDataFetch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentEndpoint, setPaymentEndpoint] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>();
  const { getTotalPrice } = useCartStore();
  const totalPrice = getTotalPrice();

  // Usar el hook para obtener los métodos de pago disponibles
  const { paymentGateways, countryInfo, isLoading: isLoadingPaymentMethods, isError: isErrorPaymentMethods } = usePaymentMethods();

  // Utilizamos el hook usePostData para hacer la petición POST al endpoint /order
  const orderMutation = postData(
    '/order',
    {}, // Headers adicionales (se usarán los headers por defecto con el token)
    {
      // Opciones adicionales de la mutación
      onSuccess: (data: any) => {
        // Puedes manejar acciones adicionales después de una orden exitosa
        console.log('Orden creada exitosamente:', data);
      },
      onError: (error: any) => {
        // Manejo de errores específicos para órdenes
        console.error('Error al crear la orden:', error);
      }
    }
  );

  const paymentLink = postData(paymentEndpoint, {}, {});

  /**
   * Función básica para guardar una nueva orden
   * @param orderData Datos de la orden a guardar
   * @returns Promise con la respuesta del servidor
   */
  const saveOrder = (orderData: OrderData) => {
    return orderMutation.mutateAsync(orderData);
  };


  // Filtrar los métodos de pago según los disponibles en la respuesta del API usando useMemo
  const paymentMethods = React.useMemo<PaymentMethod[]>(() => {
    return paymentGateways && Array.isArray(paymentGateways)
      ? paymentGateways
        .filter((gateway: string) => availablePaymentMethods[gateway])
        .map((gateway: string) => availablePaymentMethods[gateway])
      : [];
  }, [paymentGateways, availablePaymentMethods]);

  // Función para manejar el cambio de método de pago
  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const method = event.target.value;
    setSelectedPaymentMethod(method);
    if (onPaymentMethodSelect) {
      onPaymentMethodSelect(method);
    }
  };

  /**
   * Función para procesar una orden completa
   * @param checkoutData Datos del checkout
   * @param cartItems Productos en el carrito
   * @param onSuccess Callback para manejar el éxito
   * @param onError Callback para manejar errores
   */
  const handleProcessOrder = async (
    checkoutData: CheckoutData,
    cartItems: Product[],
    onSuccess: (response: any) => void,
    onError: (error: Error) => void
  ) => {
    try {
      setIsProcessing(true);

      // Convertir los productos del carrito al formato esperado por la API
      const orderItems: OrderItem[] = cartItems.map(item => ({
        product_id: item._id,
        quantity: item.quantity || 1
      }));

      let paymentProvider = undefined;
      let paymentMethod = '';
     
      
      if (checkoutData.paymentMethod === "wompi") {
        paymentProvider = "WOMPI";
        paymentMethod = 'PROVIDER'
        setPaymentEndpoint("/wompi");
      } else if (checkoutData.paymentMethod === "stripe") {
        paymentMethod = 'PROVIDER'
        paymentProvider = "STRIPE";
        setPaymentEndpoint("/stripe");
      } else if (checkoutData.paymentMethod === "coinpayment") {
        paymentMethod = 'PROVIDER'
        paymentProvider = "COIN";
        setPaymentEndpoint("/coinpayment");
      } else if (checkoutData.paymentMethod === "cash_payment") {
        paymentMethod = "CASH";
      }else if (checkoutData.paymentMethod === "pse_payment" || checkoutData.paymentMethod === "card_payment" || checkoutData.paymentMethod === "cash_efecty" || checkoutData.paymentMethod === "bank_transfer" || checkoutData.paymentMethod === "nequi_payment") {
        paymentMethod = 'PROVIDER'
        paymentProvider = "MONNET";
        setPaymentEndpoint("/monnet");
      }

      if (!checkoutData.paymentMethod) {
          onError(new Error("Error al crear la orden"));
          return;
      }


      const currency = cartItems.length > 0 && cartItems[0].currency ? cartItems[0].currency : 'COP';
      

   
      // Preparar los datos de la orden
      const orderData: OrderData = {
        address_id: checkoutData.addressId === 'new' ? null : checkoutData.addressId,
        items: orderItems,
        shipping: checkoutData.shippingInfo,
        payment_provider: paymentProvider,
        payment_method: paymentMethod,
        delivery_type: checkoutData.deliveryType,
        currency: currency,
        payment_channel: checkoutData.paymentMethod,
        carrier_id: checkoutData.carrierId,
        shipping_cost: checkoutData.shippingCost ?? 0,
      };
      
  
      // Guardar la orden usando el hook
      const orderResponse = await saveOrder(orderData);
      if (orderResponse?.data?.message === "create_order_error") {
        onError(new Error("Error al crear la orden"));
        return;
      }

      // Verificar si la respuesta contiene el ID de la orden
      if (orderResponse?.order?.id) {
        if (paymentMethod === 'PROVIDER') {
          const orderId = orderResponse.order.id;

          let payinMethod = '';
          if (checkoutData.paymentMethod === "pse_payment") {
            payinMethod = "BankTransfer";
          }else if (checkoutData.paymentMethod === "card_payment") {
            payinMethod = "TCTD";
          }else if (checkoutData.paymentMethod === "cash_efecty") {
            payinMethod = "Cash";
          }else if( checkoutData.paymentMethod === "bank_transfer"){
            payinMethod = "CashByBankTransfer";
          }else if( checkoutData.paymentMethod === "nequi_payment"){
            payinMethod = "Wallet";
          }
          // Preparar los datos para el endpoint de pago
          const paymentData = {
            title: `Order #${orderId.substring(orderId.length - 6)} - Multyhealth Vision`,
            orderId: orderId,
            payinMethod
          };


          try {
            const paymentResponse = await paymentLink.mutateAsync(paymentData);

            // Determinar la URL de redirección según el proveedor de pago
            let redirectUrl = '';

            if (checkoutData.paymentMethod === 'wompi' && paymentResponse?.paymentLink?.id) {
              redirectUrl = `https://checkout.wompi.co/l/${paymentResponse.paymentLink.id}`;
            } else if ((checkoutData.paymentMethod === 'stripe' || checkoutData.paymentMethod === 'coinpayment' || checkoutData.paymentMethod === "pse_payment" || checkoutData.paymentMethod === "card_payment" || checkoutData.paymentMethod === "cash_efecty" || checkoutData.paymentMethod === "bank_transfer"  || checkoutData.paymentMethod === "nequi_payment") && paymentResponse?.url) {
              redirectUrl = paymentResponse.url;
            }

            // Incluir la información de pago y la URL de redirección en la respuesta
            const completeResponse = {
              order: orderResponse,
              payment: paymentResponse,
              redirectUrl: redirectUrl
            };

            // Notificar éxito con la respuesta completa que incluye la URL de redirección
            onSuccess({
              ...orderResponse,
              redirectUrl: redirectUrl
            });

            // Si hay una URL de redirección, redirigir automáticamente
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }

            return completeResponse;
          } catch (paymentError) {
            console.error('Error al generar el enlace de pago:', paymentError);
            // Aún así devolvemos la orden creada
            onSuccess(orderResponse);
            return orderResponse;
          }
        }else {
          window.location.href = '/orders';
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        onError(err);
      } else {
        onError(new Error('Error desconocido al procesar la orden'));
      }
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  // Extraer estados de la mutación para exponer al componente
  const { isPending: isLoading, isError, error } = orderMutation;

  return {
    isProcessing,
    isLoading,
    isError,
    error,
    paymentGateways,
    selectedPaymentMethod,
    paymentMethods,
    availablePaymentMethods,
    isLoadingPaymentMethods,
    isErrorPaymentMethods,
    totalPrice,
    countryInfo,
    setSelectedPaymentMethod,
    handlePaymentMethodChange,
    handleProcessOrder,
  };
};